/* sz-rd-search.js — predictive search for the redesign.
 *
 * Mounts on any [data-sz-search] root. Two modes, one code path:
 *   overlay — header icon opens a full-screen dialog (rendered once in
 *             layout/sz-redesign.liquid, so it exists on every page)
 *   inline  — the /search page's own form grows a dropdown under it
 *
 * PROGRESSIVE ENHANCEMENT IS THE CONTRACT. The header icon keeps its real
 * href="/search" and the /search form keeps its native GET submit. With JS
 * off, or if this file 404s, both still work exactly as they did before this
 * file existed. Nothing here calls preventDefault() on a submit — GOTCHAS #75
 * is that trap and it applies to every form in this theme, not just the offer.
 *
 * DATA SOURCE: Shopify's native /search/suggest.json. No app, no index, no
 * third party. Verified against the live store 2026-08-24 — the response
 * shape below is measured, not assumed:
 *
 *   products[]    price / price_min / price_max are DECIMAL STRINGS ("25.00"),
 *                 NOT cents. Do not multiply by 100 and do not pass them
 *                 through a cents formatter — that is a 100x price on screen.
 *                 The product-type field is `type`, not `product_type`.
 *                 `image` is a plain CDN URL string. `compare_at_price` is
 *                 absent; compare_at_price_min/max are the real fields.
 *   articles[]    title, url, summary_html, image
 *   collections[] title, url, featured_image (NO flat `image` key)
 *   pages[]       came back empty for every probe query; requested anyway so
 *                 it starts working the day a page becomes discoverable.
 *
 * Money is formatted by taking the shop's own rendered `{{ 0 | money }}` and
 * swapping its number for ours, so the currency symbol, separators and any
 * " AUD" suffix stay whatever the merchant configured. No hardcoded '$'.
 */
(function () {
  'use strict';

  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 200;
  var LIMIT = 6;
  var MAX_RESCUE_TRIES = 2;

  /* Same list sz-rd-offer.js uses for its modal — kept identical on purpose so
     both dialogs in this theme agree on what "focusable" means. */
  var FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  /* Shopify prefixes routes on locale-scoped storefronts (/en-au/search).
     window.Shopify.routes.root carries that prefix; '/' is the fallback. */
  function root() {
    var s = window.Shopify;
    return (s && s.routes && s.routes.root) || '/';
  }

  function cfg(el, name, fallback) {
    var v = el.getAttribute('data-sz-search-' + name);
    return v === null || v === '' ? fallback : v;
  }

  /* --- money ------------------------------------------------------------ */
  /* `sample` is the shop's own `{{ 0 | money }}`, e.g. "$0.00" or "$0.00 AUD".
     Replacing its first run of digits keeps symbol, order and suffix intact. */
  function money(sample, amount) {
    if (amount === undefined || amount === null || amount === '') return '';
    if (!sample) return String(amount);
    return sample.replace(/[\d][\d.,]*/, String(amount));
  }

  function esc(s) {
    return String(s === null || s === undefined ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* Shopify CDN images take a width param; asking for a small one keeps the
     panel light on mobile data. Leaves non-CDN URLs untouched. */
  function thumb(url, px) {
    if (!url) return '';
    if (url.indexOf('cdn.shopify.com') === -1) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + px;
  }

  /* --- fetching --------------------------------------------------------- */
  function suggestUrl(q) {
    return root() + 'search/suggest.json'
      + '?q=' + encodeURIComponent(q)
      + '&resources[type]=product,article,collection,page'
      + '&resources[limit]=' + LIMIT
      + '&resources[options][unavailable_products]=last';
  }

  function countOf(results) {
    if (!results) return 0;
    return (results.products || []).length + (results.articles || []).length
      + (results.collections || []).length + (results.pages || []).length;
  }

  /* --- no-results rescue ------------------------------------------------ */
  /* Pure: given a query that found nothing, propose looser ones to try.
     Multi-word queries usually fail on the LAST word ("red king kong male"),
     so drop from the right first, then try the longest single word. A long
     single word that fails is usually a typo near the end, so try its stem. */
  function relaxations(q) {
    var out = [];
    var words = String(q).trim().split(/\s+/).filter(Boolean);
    if (words.length > 1) {
      out.push(words.slice(0, -1).join(' '));
      var longest = words.slice().sort(function (a, b) { return b.length - a.length; })[0];
      if (longest && out.indexOf(longest) === -1) out.push(longest);
    } else if (words.length === 1 && words[0].length >= 5) {
      out.push(words[0].slice(0, Math.max(4, words[0].length - 2)));
    }
    return out.slice(0, MAX_RESCUE_TRIES);
  }

  /* --- rendering -------------------------------------------------------- */
  function productRow(p, sample) {
    var varies = p.price_min && p.price_max && p.price_min !== p.price_max;
    var price = money(sample, p.price_min || p.price);
    var img = thumb(p.image || (p.featured_image && p.featured_image.url), 120);
    return ''
      + '<a class="sz-rd-sg__row sz-rd-sg__row--product" href="' + esc(p.url) + '" data-sz-sg-item>'
      + (img
        ? '<img class="sz-rd-sg__thumb" src="' + esc(img) + '" alt="" loading="lazy" width="48" height="48">'
        : '<span class="sz-rd-sg__thumb sz-rd-sg__thumb--empty" aria-hidden="true"></span>')
      + '<span class="sz-rd-sg__text">'
      + '<span class="sz-rd-sg__title">' + esc(p.title) + '</span>'
      + (price
        ? '<span class="sz-rd-sg__price">' + (varies ? 'From ' : '') + esc(price) + '</span>'
        : '')
      + '</span>'
      + (p.available === false ? '<span class="sz-rd-sg__flag">Sold out</span>' : '')
      + '</a>';
  }

  function simpleRow(item, kind) {
    var label = kind === 'article' ? 'Guide' : kind === 'collection' ? 'Collection' : 'Page';
    return ''
      + '<a class="sz-rd-sg__row sz-rd-sg__row--' + kind + '" href="' + esc(item.url) + '" data-sz-sg-item>'
      + '<span class="sz-rd-sg__text"><span class="sz-rd-sg__title">' + esc(item.title) + '</span></span>'
      + '<span class="sz-rd-sg__kind">' + label + ' &rarr;</span>'
      + '</a>';
  }

  function group(label, rows) {
    if (!rows) return '';
    return '<div class="sz-rd-sg__group"><h3 class="sz-rd-sg__grouphead">' + esc(label) + '</h3>' + rows + '</div>';
  }

  function renderResults(results, sample) {
    var html = '';
    html += group('Shrimp & supplies', (results.products || []).map(function (p) { return productRow(p, sample); }).join(''));
    html += group('Collections', (results.collections || []).map(function (c) { return simpleRow(c, 'collection'); }).join(''));
    html += group('Care guides', (results.articles || []).map(function (a) { return simpleRow(a, 'article'); }).join(''));
    html += group('Pages', (results.pages || []).map(function (p) { return simpleRow(p, 'page'); }).join(''));
    return html;
  }

  /* --- mount ------------------------------------------------------------ */
  function mount(rootEl) {
    var input = rootEl.querySelector('[data-sz-search-input]');
    var panel = rootEl.querySelector('[data-sz-search-panel]');
    var resultsBox = rootEl.querySelector('[data-sz-search-results]');
    var defaultBox = rootEl.querySelector('[data-sz-search-default]');
    var status = rootEl.querySelector('[data-sz-search-status]');
    var viewAll = rootEl.querySelector('[data-sz-search-viewall]');
    var noteBox = rootEl.querySelector('[data-sz-search-note]');
    if (!input || !panel || !resultsBox) return;

    var moneySample = cfg(rootEl, 'money', '');
    var isOverlay = cfg(rootEl, 'mode', 'inline') === 'overlay';
    var timer = null;
    var controller = null;
    var lastReturn = null;

    function say(msg) { if (status) status.textContent = msg; }

    function note(html) {
      if (!noteBox) return;
      noteBox.innerHTML = html || '';
      noteBox.hidden = !html;
    }

    function showDefault() {
      resultsBox.innerHTML = '';
      note('');
      if (defaultBox) defaultBox.hidden = false;
      if (viewAll) viewAll.hidden = true;
      panel.hidden = false;
      say('');
    }

    function paint(results, q, rescuedFrom) {
      if (defaultBox) defaultBox.hidden = true;
      resultsBox.innerHTML = renderResults(results, moneySample);
      note(rescuedFrom
        ? 'No matches for <b>' + esc(rescuedFrom) + '</b>. Showing results for <b>' + esc(q) + '</b> instead.'
        : '');
      if (viewAll) {
        viewAll.hidden = false;
        viewAll.setAttribute('href', root() + 'search?q=' + encodeURIComponent(q));
      }
      panel.hidden = false;
      var n = countOf(results);
      say(n + (n === 1 ? ' result' : ' results') + ' for ' + q);
    }

    /* Nothing found and nothing rescuable: say so, then fall back to the
       curated picks so the panel is never a dead end. */
    function paintEmpty(q) {
      resultsBox.innerHTML = '';
      note('Nothing matched <b>' + esc(q) + '</b>. Try a colour or line name &mdash; '
        + '&ldquo;red&rdquo;, &ldquo;king kong&rdquo;, &ldquo;tiger&rdquo; &mdash; or start here:');
      if (defaultBox) defaultBox.hidden = false;
      if (viewAll) viewAll.hidden = true;
      panel.hidden = false;
      say('No results for ' + q);
    }

    /* One in-flight request at a time. Without the abort, a slow response for
       "re" can land AFTER the fast one for "red kong" and overwrite it — the
       race that makes a search box feel possessed. */
    function fetchSuggest(q) {
      if (controller) controller.abort();
      controller = typeof AbortController === 'function' ? new AbortController() : null;
      return fetch(suggestUrl(q), {
        signal: controller ? controller.signal : undefined,
        headers: { Accept: 'application/json' }
      }).then(function (r) {
        if (!r.ok) throw new Error('suggest ' + r.status);
        return r.json();
      }).then(function (d) {
        return (d && d.resources && d.resources.results) || {};
      });
    }

    function run(q) {
      rootEl.setAttribute('data-sz-search-busy', '');
      fetchSuggest(q).then(function (results) {
        if (countOf(results) > 0) { paint(results, q); return null; }
        /* Rescue: walk the looser queries until one finds something. */
        var chain = Promise.resolve(null);
        relaxations(q).forEach(function (alt) {
          chain = chain.then(function (found) {
            if (found) return found;
            return fetchSuggest(alt).then(function (r2) {
              return countOf(r2) > 0 ? { q: alt, results: r2 } : null;
            }).catch(function () { return null; });
          });
        });
        return chain.then(function (found) {
          if (found) paint(found.results, found.q, q);
          else paintEmpty(q);
        });
      }).catch(function (err) {
        if (err && err.name === 'AbortError') return;
        /* A failed suggest must never break the box: the form still submits. */
        showDefault();
      }).then(function () {
        rootEl.removeAttribute('data-sz-search-busy');
      });
    }

    function onInput() {
      var q = input.value.trim();
      if (timer) clearTimeout(timer);
      if (q.length < MIN_CHARS) {
        if (controller) controller.abort();
        showDefault();
        return;
      }
      timer = setTimeout(function () { run(q); }, DEBOUNCE_MS);
    }

    /* --- keyboard ------------------------------------------------------- */
    function items() {
      return Array.prototype.slice.call(panel.querySelectorAll('[data-sz-sg-item]'));
    }

    function move(dir) {
      var list = items();
      if (!list.length) return;
      var i = list.indexOf(document.activeElement);
      var next = i === -1 ? (dir > 0 ? 0 : list.length - 1) : i + dir;
      if (next < 0) { input.focus(); input.select(); return; }
      if (next >= list.length) next = list.length - 1;
      list[next].focus();
    }

    /* The dialog declares aria-modal="true". A modal that lets Tab wander onto
       the page behind it is lying to assistive tech — the user is told the
       rest of the page is inert while their focus walks straight into it. */
    function trapTab(e) {
      var f = Array.prototype.filter.call(
        rootEl.querySelectorAll(FOCUSABLE),
        function (el) { return el.offsetParent !== null || el === document.activeElement; }
      );
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    rootEl.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
      else if (e.key === 'Tab' && isOverlay) { trapTab(e); }
      else if (e.key === 'Escape') {
        e.preventDefault();
        if (isOverlay) close();
        else panel.hidden = true;
      }
    });

    input.addEventListener('input', onInput);
    input.addEventListener('focus', function () {
      if (input.value.trim().length < MIN_CHARS) showDefault();
    });

    /* --- overlay open/close --------------------------------------------- */
    function open() {
      lastReturn = document.activeElement;
      rootEl.hidden = false;
      document.documentElement.classList.add('sz-rd-search-open');
      showDefault();
      /* rAF so the element is laid out before focus — iOS Safari refuses to
         raise the keyboard for an element that was display:none this tick. */
      requestAnimationFrame(function () { input.focus(); });
    }

    function close() {
      rootEl.hidden = true;
      document.documentElement.classList.remove('sz-rd-search-open');
      if (controller) controller.abort();
      if (lastReturn && lastReturn.focus) lastReturn.focus();
    }

    if (isOverlay) {
      rootEl.szSearchOpen = open;
      Array.prototype.forEach.call(rootEl.querySelectorAll('[data-sz-search-close]'), function (btn) {
        btn.addEventListener('click', function (e) { e.preventDefault(); close(); });
      });
    } else {
      /* Inline: clicking away closes the dropdown but leaves the query. */
      document.addEventListener('click', function (e) {
        if (!rootEl.contains(e.target)) panel.hidden = true;
      });
    }
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sz-search]'), mount);

    /* The header icon is a real link. We only hijack a plain left-click, so
       middle-click, ctrl/cmd-click and "open in new tab" keep working. */
    var overlay = document.querySelector('[data-sz-search-mode="overlay"]');
    if (!overlay || !overlay.szSearchOpen) return;
    Array.prototype.forEach.call(document.querySelectorAll('[data-sz-search-open]'), function (trigger) {
      trigger.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        overlay.szSearchOpen();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
